import React, { useState } from 'react';
import { theme } from '../theme';
import LoadingSpinner from './LoadingSpinner';

const TabbedIO = ({ input, output, onInputChange, loading, error, errorType, autoSwitchToOutput = false }) => {
  const [activeTab, setActiveTab] = useState('input');

  // Auto-switch to output tab when loading starts
  React.useEffect(() => {
    if (autoSwitchToOutput && loading) {
      setActiveTab('output');
    }
  }, [autoSwitchToOutput, loading]);

  const tabs = [
    { id: 'input', label: 'Input', count: input.length },
    { id: 'output', label: 'Output', count: output.length }
  ];

  return (
    <div 
      className="flex flex-col h-full"
      style={{ backgroundColor: theme.colors.bg.secondary }}
    >
      {/* Tab Headers */}
      <div className="flex border-b" style={{ borderColor: theme.colors.border.primary }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="px-4 py-2 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === tab.id 
                ? theme.colors.text.primary 
                : theme.colors.text.secondary,
              backgroundColor: activeTab === tab.id 
                ? theme.colors.bg.tertiary 
                : 'transparent',
              borderBottom: activeTab === tab.id 
                ? `2px solid ${theme.colors.accent.blue}` 
                : '2px solid transparent',
              transition: theme.transitions.base
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'input' && (
          <div className="h-full p-4">
            <textarea
              value={input}
              onChange={(e) => onInputChange(e.target.value)}
              placeholder="Enter input for your program..."
              className="w-full h-full resize-none bg-transparent border-none outline-none"
              style={{
                color: theme.colors.text.primary,
                fontFamily: theme.typography.fontFamily.mono.join(', '),
                fontSize: theme.typography.fontSize.base[0],
                lineHeight: theme.typography.fontSize.base[1].lineHeight,
                backgroundColor: theme.colors.bg.quaternary,
                padding: theme.spacing[4],
                borderRadius: theme.borderRadius.base,
                border: `1px solid ${theme.colors.border.primary}`
              }}
            />
          </div>
        )}

        {activeTab === 'output' && (
          <div className="h-full p-4 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner size="md" text="Compiling..." />
              </div>
            ) : error ? (
              <div 
                className="p-3 rounded-md border"
                style={{
                  backgroundColor: `${theme.colors.accent.red}10`,
                  borderColor: theme.colors.accent.red,
                  color: theme.colors.accent.red
                }}
              >
                <div className="font-medium mb-1">
                  {errorType === 'compile' ? 'Compilation Error' : errorType === 'runtime' ? 'Runtime Error' : 'Error'}
                </div>
                <div className="text-sm font-mono whitespace-pre-wrap">{error}</div>
              </div>
            ) : output ? (
              <div 
                className="font-mono text-sm whitespace-pre-wrap"
                style={{ 
                  color: theme.colors.text.primary,
                  fontFamily: theme.typography.fontFamily.mono.join(', '),
                  backgroundColor: theme.colors.bg.quaternary,
                  padding: theme.spacing[4],
                  borderRadius: theme.borderRadius.base,
                  border: `1px solid ${theme.colors.border.primary}`,
                  minHeight: '100px'
                }}
              >
                {output.trim() || '(No output)'}
              </div>
            ) : (
              <div 
                className="flex items-center justify-center h-full"
                style={{ color: theme.colors.text.tertiary }}
              >
                <div className="text-center">
                  <div className="mb-2">📤</div>
                  <div className="text-sm">Output will appear here</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TabbedIO;
